/*
 * Nimall product shelf reliability layer.
 *
 * Keeps the accepted Halo product-card markup while avoiding full collection
 * downloads for twelve-card homepage shelves. It also prevents a failed request
 * from leaving a shelf permanently locked in its loading state.
 */
(function (window, document, $) {
  'use strict';

  const CARD_MARKER = '<div class="product">';
  const MAX_CONCURRENT = 2;
  const MAX_ATTEMPTS = 2;
  const SHELF_PRODUCT_LIMIT = 14;
  const SHELF_CANDIDATE_LIMIT = 36;
  const REQUEST_TIMEOUT_MS = 8000;
  const PRELOAD_DISTANCE_PX = 1200;
  const CARD_IMAGE_PRELOAD_DISTANCE_PX = 700;
  const JQUERY_WAIT_MS = 50;
  const JQUERY_WAIT_LIMIT = 200;
  const PRODUCT_CARD_IMAGE_SELECTOR = [
    '.template-index #MainContent .products-grid > .product img[data-srcset]',
    '.template-index #MainContent .products-grid > .product img[data-src]',
    '.template-collection #MainContent .productListing.productGrid > .product img[data-srcset]',
    '.template-collection #MainContent .productListing.productGrid > .product img[data-src]',
    '.template-search #MainContent .productListing.productGrid > .product img[data-srcset]',
    '.template-search #MainContent .productListing.productGrid > .product img[data-src]',
    '.template-product #MainContent .halo-recommendations-block .products-grid > .product img[data-srcset]',
    '.template-product #MainContent .halo-recommendations-block .products-grid > .product img[data-src]',
    '.template-product #MainContent .recentlyViewed-row.products-grid > .product img[data-srcset]',
    '.template-product #MainContent .recentlyViewed-row.products-grid > .product img[data-src]'
  ].join(',');
  const completedMarkup = new Map();
  const queue = [];
  let activeRequests = 0;
  let initialized = false;
  let observedBlocks = [];
  let recoveryFrame = 0;
  let cardImageObserver;
  let cardImageMutationObserver;

  function findCardStarts(markup) {
    const starts = [];
    let offset = 0;
    while (offset < markup.length) {
      const index = markup.indexOf(CARD_MARKER, offset);
      if (index === -1) break;
      starts.push(index);
      offset = index + CARD_MARKER.length;
    }
    return starts;
  }

  function limitCardMarkup(markup, limit) {
    const starts = findCardStarts(markup);
    if (starts.length === 0) return '';
    const end = starts.length > limit ? starts[limit] : markup.length;
    return markup.slice(starts[0], end).trim();
  }

  async function fetchLimitedCards(url, limit) {
    const cacheKey = `${url}|${limit}`;
    if (completedMarkup.has(cacheKey)) return completedMarkup.get(cacheKey);

    const controller = new AbortController();
    const timeout = window.setTimeout(function () {
      controller.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      const response = await window.fetch(url, {
        cache: 'force-cache',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      if (!response.body || typeof response.body.getReader !== 'function') {
        const fallbackMarkup = limitCardMarkup(await response.text(), limit);
        completedMarkup.set(cacheKey, fallbackMarkup);
        return fallbackMarkup;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let markup = '';

      while (true) {
        const chunk = await reader.read();
        if (chunk.done) {
          markup += decoder.decode();
          break;
        }

        markup += decoder.decode(chunk.value, { stream: true });
        const starts = findCardStarts(markup);
        if (starts.length > limit) {
          const limitedMarkup = markup.slice(starts[0], starts[limit]).trim();
          try {
            await reader.cancel();
          } catch (cancelError) {
            // The useful card markup is already complete; cancellation failure is harmless.
          }
          completedMarkup.set(cacheKey, limitedMarkup);
          return limitedMarkup;
        }
      }

      const limitedMarkup = limitCardMarkup(markup, limit);
      completedMarkup.set(cacheKey, limitedMarkup);
      return limitedMarkup;
    } catch (error) {
      if (controller.signal.aborted) throw new Error('REQUEST_TIMEOUT');
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function schedule(task, priority) {
    return new Promise(function (resolve, reject) {
      const queuedTask = { task: task, resolve: resolve, reject: reject };
      if (priority) queue.unshift(queuedTask);
      else queue.push(queuedTask);
      drainQueue();
    });
  }

  function drainQueue() {
    while (activeRequests < MAX_CONCURRENT && queue.length > 0) {
      const queuedTask = queue.shift();
      activeRequests += 1;
      Promise.resolve()
        .then(queuedTask.task)
        .then(queuedTask.resolve, queuedTask.reject)
        .finally(function () {
          activeRequests -= 1;
          drainQueue();
        });
    }
  }

  function activateProductCardImage(image) {
    if (!image) return;
    const srcset = image.getAttribute('data-srcset');
    const src = image.getAttribute('data-src');
    const responsiveSrcset = srcset || image.getAttribute('srcset');

    if (srcset) {
      image.setAttribute('srcset', srcset);
      image.removeAttribute('data-srcset');
    }
    if (src) {
      image.setAttribute('src', src);
      image.removeAttribute('data-src');
    }

    // Some Halo cards expose only data-srcset. Chromium usually starts that
    // request, but Safari and constrained networks can leave the card blank
    // until a later layout pass. Give the browser a small baseline source and
    // disable its second lazy-load gate once our own near-viewport observer has
    // activated the image. Responsive selection still comes from srcset.
    if (!image.getAttribute('src') && responsiveSrcset) {
      const fallbackSrc = responsiveSrcset
        .split(',')[0]
        .trim()
        .split(/\s+/)[0];
      if (fallbackSrc) image.setAttribute('src', fallbackSrc);
    }
    image.setAttribute('loading', 'eager');
    image.setAttribute('decoding', 'async');

    image.classList.remove('lazyload', 'lazyloading');
    image.classList.add('lazyloaded');
  }

  function activateInsertedImages($content) {
    $content.find('img[data-srcset], img[data-src]').each(function () {
      activateProductCardImage(this);
    });
  }

  function observeProductCardImages(root) {
    if (!root || !root.querySelectorAll) return;
    const images = Array.from(root.querySelectorAll(PRODUCT_CARD_IMAGE_SELECTOR));
    if (root.matches && root.matches(PRODUCT_CARD_IMAGE_SELECTOR)) images.unshift(root);

    images.forEach(function (image) {
      if (cardImageObserver) cardImageObserver.observe(image);
      else activateProductCardImage(image);
    });
  }

  function initializeProductCardImageRecovery() {
    if ('IntersectionObserver' in window) {
      cardImageObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          activateProductCardImage(entry.target);
          cardImageObserver.unobserve(entry.target);
        });
      }, {
        rootMargin: `${CARD_IMAGE_PRELOAD_DISTANCE_PX}px 0px`,
        threshold: 0
      });
    }

    observeProductCardImages(document);
    const main = document.querySelector('#MainContent');
    if (!main || !('MutationObserver' in window)) return;

    cardImageMutationObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) observeProductCardImages(node);
        });
      });
    });
    cardImageMutationObserver.observe(main, { childList: true, subtree: true });
  }

  function finishProductEnhancements($content, $block) {
    const shared = window.sharedFunctions || {};
    if (window.wishlist && window.wishlist.show && shared.setLocalStorageProductForWishlist) {
      shared.setLocalStorageProductForWishlist();
    }
    if (shared.checkNeedToConvertCurrency && shared.checkNeedToConvertCurrency()) {
      window.Currency.convertAll(
        window.shop_currency,
        $('#currencies .active').attr('data-currency'),
        'span.money',
        'money_format'
      );
    }
    if (shared.swapHoverVideoProductCard) shared.swapHoverVideoProductCard();

    if ($content.hasClass('products-carousel') && shared.productBlockSilder) {
      shared.productBlockSilder($content.parent());
    } else if (
      $content.hasClass('products-cursor') &&
      $content.data('enable-hover') === true &&
      shared.productBlockScroller
    ) {
      shared.productBlockScroller($content);
    }

    if ($('body').hasClass('cursor-fixed__show') && window.sharedFunctionsAnimation) {
      window.sharedFunctionsAnimation.onEnterButton();
      window.sharedFunctionsAnimation.onLeaveButton();
    }

    $block.removeClass('nimall-shelf-loading nimall-shelf-failed');
  }

  function productHandle(raw) {
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      return match ? decodeURIComponent(match[1]).toLowerCase() : '';
    } catch (error) {
      return '';
    }
  }

  function uniqueProductsById(products, excludeCurrent) {
    const seen = new Set();
    const handles = new Set();
    const current = excludeCurrent ? productHandle(window.location.href) : '';
    return products.filter(function (product) {
      const item = product.querySelector('.product-item[data-product-id]');
      const id = item && item.getAttribute('data-product-id');
      const link = product.querySelector('a[href*="/products/"]');
      const handle = link && productHandle(link.getAttribute('href'));
      if ((current && handle === current) || (id && seen.has(id)) || (handle && handles.has(handle))) return false;
      if (id) seen.add(id);
      if (handle) handles.add(handle);
      return true;
    });
  }

  function renderProducts($content, $block, markup, limit) {
    const $products = $(markup).filter('.product');
    if ($products.length === 0) return false;

    const $panel = $content.closest('[data-product-TabContent]');
    const rotation = window.NimallPublicProductRotation;
    const identity = $panel.attr('id') || $block.attr('id') || 'shopify-product-shelf';
    const ordered = rotation && rotation.orderElements
      ? rotation.orderElements($products.toArray(), identity)
      : $products.toArray();
    const products = $(uniqueProductsById(ordered)).slice(0, limit);
    $content.html(products);
    activateInsertedImages($content);
    $panel
      .addClass('loaded')
      .removeClass('nimall-shelf-failed')
      .attr('data-nimall-shelf-state', 'loaded');
    finishProductEnhancements($content, $block);
    return true;
  }

  function loadProductRecommendationsEarly() {
    const block = document.querySelector('[data-recommendations-block]');
    if (!block) return;

    const state = block.getAttribute('data-nimall-recommendations-state');
    if (state === 'loading' || state === 'loaded') return;

    const url = block.getAttribute('data-url');
    const limit = Number(block.getAttribute('data-limit')) || 10;
    const $block = $(block);
    const $grid = $block.find('.products-grid').first();
    if (!url || !$grid.length) return;

    block.setAttribute('data-nimall-recommendations-state', 'loading');
    schedule(async function () {
      let lastError;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        try {
          const candidateLimit = Math.min(Math.max(limit * 3, limit), SHELF_CANDIDATE_LIMIT);
          const markup = await fetchLimitedCards(url, candidateLimit);
          const $candidates = $(markup).filter('.product');
          const rotation = window.NimallPublicProductRotation;
          const ordered = rotation && rotation.orderElements
            ? rotation.orderElements($candidates.toArray(), `${block.id || 'pdp-recommendations'}|${url}`)
            : $candidates.toArray();
          const $products = $(uniqueProductsById(ordered, true)).slice(0, limit);
          if (!$products.length) throw new Error('EMPTY_RECOMMENDATIONS');

          $grid.html($products);
          activateInsertedImages($grid);
          $block.find('.wrapper-container')
            .removeClass('product-recommendations-loading has-product');
          block.setAttribute('data-nimall-recommendations-state', 'loaded');
          finishProductEnhancements($grid, $block);
          return;
        } catch (error) {
          lastError = error;
          if (attempt + 1 < MAX_ATTEMPTS) {
            await new Promise(function (resolve) {
              window.setTimeout(resolve, 500 + attempt * 700);
            });
          }
        }
      }

      // Leave the original loading markup intact so Halo's native observer can
      // still recover when the buyer reaches the recommendation section.
      block.setAttribute('data-nimall-recommendations-state', 'idle');
      if (window.console && window.console.warn) {
        window.console.warn('Nimall product recommendations preload failed', url, lastError);
      }
    }, true).catch(function (error) {
      block.setAttribute('data-nimall-recommendations-state', 'idle');
      if (window.console && window.console.warn) {
        window.console.warn('Nimall product recommendations queue failed', url, error);
      }
    });
  }

  function renderEmpty($content, $block) {
    const $panel = $content.closest('[data-product-TabContent]');
    $content.html('<p class="loading center nimall-shelf-message">Sorry, there are no products in this collection.</p>');
    $panel.addClass('loaded').attr('data-nimall-shelf-state', 'loaded');
    $block.removeClass('nimall-shelf-loading');
  }

  function renderRetry($content, $block, retry) {
    const $panel = $content.closest('[data-product-TabContent]');
    $content.html(
      '<div class="nimall-shelf-fallback" role="status">' +
        '<p>Products are taking longer than expected.</p>' +
        '<button type="button" class="button button--secondary" data-nimall-shelf-retry>Reload products</button>' +
      '</div>'
    );
    $panel
      .addClass('loaded nimall-shelf-failed')
      .attr('data-nimall-shelf-state', 'error');
    $block.removeClass('nimall-shelf-loading').addClass('nimall-shelf-failed');
    $content
      .off('click.nimallShelfRetry', '[data-nimall-shelf-retry]')
      .on('click.nimallShelfRetry', '[data-nimall-shelf-retry]', function () {
        retry();
      });
  }

  function requestPanel($block, $link, $panel, priority) {
    if (!$panel.length || !$link.length) return;

    const panel = $panel.get(0);
    const state = panel.getAttribute('data-nimall-shelf-state');
    if (state === 'queued' || state === 'loading' || state === 'loaded') return;

    const url = $link.attr('data-href');
    const limit = SHELF_PRODUCT_LIMIT;
    const $content = $panel.find('.products-load').first();
    if (!url || url === '?view=ajax_product_block' || !$content.length) {
      renderEmpty($content, $block);
      return;
    }

    panel.setAttribute('data-nimall-shelf-state', 'queued');
    $panel.removeClass('loaded nimall-shelf-failed');
    $block.removeClass('nimall-shelf-failed').addClass('nimall-shelf-loading');

    schedule(async function () {
      panel.setAttribute('data-nimall-shelf-state', 'loading');
      let lastError;

      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        try {
          const markup = await fetchLimitedCards(url, SHELF_CANDIDATE_LIMIT);
          if (markup && renderProducts($content, $block, markup, limit)) return;
          renderEmpty($content, $block);
          return;
        } catch (error) {
          lastError = error;
          if (attempt + 1 < MAX_ATTEMPTS) {
            await new Promise(function (resolve) {
              window.setTimeout(resolve, 500 + attempt * 700);
            });
          }
        }
      }

      panel.setAttribute('data-nimall-shelf-state', 'error');
      renderRetry($content, $block, function () {
        panel.setAttribute('data-nimall-shelf-state', 'idle');
        requestPanel($block, $link, $panel, true);
      });
      if (window.console && window.console.warn) {
        window.console.warn('Nimall product shelf load failed', url, lastError);
      }
    }, priority).catch(function (error) {
      panel.setAttribute('data-nimall-shelf-state', 'error');
      renderRetry($content, $block, function () {
        panel.setAttribute('data-nimall-shelf-state', 'idle');
        requestPanel($block, $link, $panel, true);
      });
      if (window.console && window.console.warn) {
        window.console.warn('Nimall product shelf queue failed', url, error);
      }
    });
  }

  function collectionUrlFromTab($link) {
    const raw = $link.attr('data-href');
    if (!raw || raw === '?view=ajax_product_block') return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (!url.pathname.startsWith('/collections/')) return '';
      return url.pathname;
    } catch (error) {
      return '';
    }
  }

  function syncViewAll($block, $link) {
    const href = collectionUrlFromTab($link);
    const $viewAll = $block.find('.halo-block-header .view_all').first();
    if (!href || !$viewAll.length) return;
    $viewAll
      .attr('href', href)
      .removeAttr('aria-disabled')
      .removeAttr('role');
  }

  function activateBlock(block, priority) {
    // Hidden desktop shelves have a zero rectangle on mobile. They must stay
    // idle until the responsive layout actually makes them visible.
    if (!block.getClientRects().length) return;
    const $block = $(block);
    const $link = $block.find('.list-product-tabs .tab-links.active').first();
    const $panel = $block.find('.product-tabs-content .tab-content.active').first();
    syncViewAll($block, $link);
    requestPanel($block, $link, $panel, priority);
  }

  function bindTabs(block) {
    const $block = $(block);
    const $links = $block.find('.list-product-tabs [data-product-tabTop]');
    const $panels = $block.find('[data-product-TabContent]');

    $links
      .off('click.nimallShelf')
      .on('click.nimallShelf', function (event) {
        event.preventDefault();
        event.stopPropagation();

        const $link = $(this);
        if ($link.hasClass('active')) return;

        const $panel = $($link.data('target'));
        $links.removeClass('active');
        $panels.removeClass('active');
        $link.addClass('active');
        $panel.addClass('active');

        syncViewAll($block, $link);
        requestPanel($block, $link, $panel, true);

        const $slider = $panel.find('.slick-slider.slick-initialized');
        if ($slider.length) $slider.slick('refresh');
      });
  }

  function recoverNearbyBlocks() {
    if (recoveryFrame) return;
    recoveryFrame = window.requestAnimationFrame(function () {
      recoveryFrame = 0;
      observedBlocks.forEach(function (block) {
        const section = block.closest('.shopify-section') || block;
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight + PRELOAD_DISTANCE_PX && rect.bottom > -300) {
          activateBlock(block, false);
        }
      });
    });
  }

  function initializeReliabilityLayer() {
    if (initialized) {
      recoverNearbyBlocks();
      return;
    }

    initialized = true;
    initializeProductCardImageRecovery();
    loadProductRecommendationsEarly();
    observedBlocks = Array.from(document.querySelectorAll('[data-product-tab-block]'));
    observedBlocks.forEach(bindTabs);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) activateBlock(entry.target, false);
        });
      }, { rootMargin: `${PRELOAD_DISTANCE_PX}px 0px`, threshold: 0 });
      observedBlocks.forEach(function (block) {
        observer.observe(block);
      });
    }

    recoverNearbyBlocks();
    window.addEventListener('scroll', recoverNearbyBlocks, { passive: true });
    window.addEventListener('resize', recoverNearbyBlocks, { passive: true });
    window.addEventListener('load', recoverNearbyBlocks, { once: true });
    window.addEventListener('pageshow', recoverNearbyBlocks);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) recoverNearbyBlocks();
    });
  }

  function initializeWhenReady(attempt) {
    $ = window.jQuery;
    if (!$) {
      if (attempt < JQUERY_WAIT_LIMIT) {
        window.setTimeout(function () {
          initializeWhenReady(attempt + 1);
        }, JQUERY_WAIT_MS);
      }
      return;
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeReliabilityLayer, { once: true });
    } else {
      initializeReliabilityLayer();
    }
  }

  // Claim every existing tab shelf before Halo's DOM-ready handler creates its
  // observer. Halo then leaves these blocks alone and this layer owns loading.
  observedBlocks = Array.from(document.querySelectorAll('[data-product-tab-block]'));
  observedBlocks.forEach(function (block) {
    block.setAttribute('data-limit', String(SHELF_PRODUCT_LIMIT));
    block.classList.add('ajax-loaded', 'nimall-shelf-reliability-owned');
  });

  window.NimallShelfReliability = {
    version: 'N466-P5',
    recover: recoverNearbyBlocks,
    getState: function () {
      return {
        activeRequests: activeRequests,
        queuedRequests: queue.length,
        cachedShelves: completedMarkup.size,
        blocks: observedBlocks.map(function (block) {
          const activePanel = block.querySelector('[data-product-TabContent].active');
          return {
            sectionId: block.getAttribute('sectionid'),
            state: activePanel ? activePanel.getAttribute('data-nimall-shelf-state') : null,
            products: activePanel ? activePanel.querySelectorAll('.products-load > .product').length : 0
          };
        })
      };
    }
  };

  initializeWhenReady(0);
})(window, document, window.jQuery);
